import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { TranslocoModule } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { ProfileService } from 'app/services/profile.service';
import {
  FuseConfig,
  FuseConfigService,
  Scheme,
  Theme,
  Themes,
} from '@fuse/services/config';
@Component({
  selector: 'user',
  templateUrl: './user.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'user',
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    NgClass,
    MatDividerModule,
    TranslocoModule,
  ],
})
export class UserComponent implements OnInit, OnDestroy {
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _router = inject(Router);
  private _userService = inject(UserService);
  private _profileService = inject(ProfileService);
  private _fuseConfigService = inject(FuseConfigService);

  static ngAcceptInputType_showAvatar: BooleanInput;
  config: FuseConfig;
  layout: string;
  scheme: 'dark' | 'light';
  theme: string;
  themes: Themes;
  @Input() showAvatar = true;
  user: User;
  userAvatar: string | null = null;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit(): void {
    // Load the user's profile picture from localStorage if available
    this.userAvatar = localStorage.getItem('currentUserPfp');
    // Fetch the user profile data and avatar
    this.fetchUserProfile();

    // Subscribe to user changes
    this._userService.user$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((user: User) => {
        this.user = user;

        // If the avatar is not available in localStorage, fall back to user's avatar
        if (!this.userAvatar && user.avatar) {
          this.userAvatar = user.avatar;
        }

        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    this._fuseConfigService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config: FuseConfig) => {
        // Store the config
        this.config = config;
        this.scheme = config.scheme == 'dark' ? 'dark' : 'light';
      });

    const savedScheme = localStorage.getItem('userTheme') as Scheme;
    if (savedScheme) {
      this.setScheme(savedScheme);
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Fetch the user's profile data using the ProfileService
   */
  fetchUserProfile(): void {
    this._profileService
      .getProfileInfo()
      .then((data: any) => {
        const profilePictureUrl = data?.userInfo?.profilePictureURL;

        if (profilePictureUrl) {
          // Update the userAvatar with the fetched profile picture
          this.userAvatar = profilePictureUrl;

          // Store the avatar in localStorage
          localStorage.setItem('currentUserPfp', profilePictureUrl);
        }

        // Mark for check to update the UI
        this._changeDetectorRef.markForCheck();
      })
      .catch(error => {
        console.error('Error fetching user profile:', error);
      });
  }
  /**
   * Set the scheme on the config
   *
   * @param scheme
   */
  setScheme(scheme: Scheme): void {
    this._fuseConfigService.config = { scheme };
    localStorage.setItem('userTheme', scheme); // Save the scheme in localStorage
    this.scheme = scheme == 'dark' ? 'dark' : 'light';
  }
  toggleScheme(): void {
    this.scheme = this.scheme === 'dark' ? 'light' : 'dark';
    this.setScheme(this.scheme);
    //refresh page
    window.location.reload();
    //  document.documentElement.classList.toggle('dark', this.theme === 'dark');
  }

  /**
   * Set the theme on the config
   *
   * @param theme
   */
  setTheme(theme: Theme): void {
    this._fuseConfigService.config = { theme };
  }
  updateUserStatus(status: string): void {
    if (!this.user) return;

    this._userService
      .update({
        ...this.user,
        status,
      })
      .subscribe();
  }

  signOut(): void {
    this._router.navigate(['/sign-out']);
  }

  openProfile(): void {
    this._router.navigate(['/profile']);
  }
}
